import { CircleHelp, FileText, Check, ListOrdered, MessageSquareText, Lightbulb, Video } from 'lucide-react'
import { SparklesIcon, PhotoIcon } from '@heroicons/react/24/outline'
import { stripMarkdown } from '@helpers/format'
import ProgressBar from './ProgressBar'

const getBlockTypeLabel = (blockType) => {
    switch (blockType) {
        case 'FreeResponse':
        case 'Question':
            return 'Question';
        case 'Reflection':
            return 'Reflection';
        case 'Video':
            return 'Video';
        default:
            return "Reading";
    }
};

const getBlockContent = (block) => {
    return stripMarkdown(
        block.label ||
        block.question ||
        block.content ||
        block.url ||
        getDefaultContentLabel(block.__typename)
    );
};

const getDefaultContentLabel = (blockType) => {
    switch (blockType) {
        case 'FreeResponse':
        case 'Question':
        case 'Reflection':
            return 'Question';
        case 'Video':
            return 'Video';
        case 'Text':
            return 'Content';
        default:
            return blockType;
    }
};

const getIconBgColor = (blockType) => {
    switch (blockType) {
        case 'FreeResponse':
            return 'bg-orange-200';
        case 'Question':
            return 'bg-yellow-200';
        case 'Reflection':
            return 'bg-red-200';
        case 'Video':
            return 'bg-purple-200';
        case 'Text':
            return 'bg-purple-200';
        case 'Image':
            return 'bg-purple-200';
        default:
            return 'bg-muted';
    }
};

const getIconTextColor = (blockType) => {
    switch (blockType) {
        case 'FreeResponse':
            return 'text-orange-900';
        case 'Question':
            return 'text-yellow-900';
        case 'Reflection':
            return 'text-red-900';
        case 'Video':
            return 'text-purple-900';
        case 'Text':
            return 'text-purple-900';
        case 'Image':
            return 'text-purple-900';
        default:
            return 'text-muted-foreground';
    }
};

const BlockIcon = ({ blockType }) => {
    const iconClass = `size-4 ${getIconTextColor(blockType)}`;

    switch (blockType) {
        case 'FreeResponse':
            return <MessageSquareText className={iconClass} />;
        case 'Question':
            return <ListOrdered className={iconClass} />;
        case 'Reflection':
            return <Lightbulb className={iconClass} />;
        case 'Video':
            return <Video className={iconClass} />;
        case 'Text':
            return <FileText className={iconClass} />;
        case 'Image':
            return <PhotoIcon className={iconClass} />;
        default:
            return <CircleHelp className={iconClass} />;
    }
};

const BlockItem = ({ block, index, isActive, isCompleted, onClick, hasInstantFeedback }) => {
    return (
        <button
            onClick={onClick}
            className={`flex flex-row items-center justify-center gap-2 w-full text-left p-2 rounded-md hover:bg-accent ${isCompleted ? 'text-primary/40' : ''} 
                ${isActive ? 'bg-accent border-1 border-border font-semibold' : ''} mb-2`}
        >
            <div className={`flex items-center justify-center rounded-md shrink-0 p-2 ${getIconBgColor(block.__typename)}`}>
                <BlockIcon blockType={block.__typename} />
            </div>
            {isCompleted && (
                <Check className="size-4 text-primary/60 ml-1 shrink-0" />
            )}
            <p className='text-xs flex-grow line-clamp-1'>
                {getBlockContent(block)}
            </p>
            {hasInstantFeedback && (
                <SparklesIcon className='size-4 text-purple-600 dark:text-purple-300 shrink-0' />
            )}
        </button>
    );
};

const LessonOutline = ({
    projectName,
    blocks,
    blockSubmissions,
    currentBlockIndex,
    setCurrentBlockIndex,
    getBlockFeedbackState
}) => {

    return (
        <div className='px-5 h-full min-h-full overflow-y-scroll'>
            <div className='sticky top-0 py-3 bg-background'>
                <h2 className='text-md font-semibold mt-2 flex items-start'>
                    {projectName}
                </h2>

                <ProgressBar
                    currentBlockIndex={currentBlockIndex}
                    blocks={blocks}
                    blockSubmissions={blockSubmissions}
                />
            </div>

            <div className="pb-8">
                <For each='block' index='idx' of={blocks}>
                    <With blockSubmission={blockSubmissions?.find(bs => bs.blockId === block.blockId)}>
                        <BlockItem
                            key={block.id || idx}
                            block={block}
                            index={idx}
                            isActive={idx === currentBlockIndex}
                            isCompleted={blockSubmission?.status === 'COMPLETED'}
                            onClick={() => setCurrentBlockIndex(idx)}
                            hasInstantFeedback={
                                block.__typename === 'FreeResponse' &&
                                getBlockFeedbackState &&
                                getBlockFeedbackState(block.blockId)?.instantFeedbackContent
                            }
                        />
                    </With>
                </For>
            </div>
        </div>
    )
}

export default LessonOutline 
